import { LazyMotion, m } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { match, useHistory } from 'react-router'
import styled, { createGlobalStyle } from 'styled-components'
import { NavLink } from 'react-router-dom'
import { Box } from '@machobear/design-system'
import { useDispatch } from '@/hooks/redux'
import config from '@/config'
import useMediaQuery from '@/hooks/use-media-query'
import useRole from '@/hooks/use-role'
import { colors, breakpoints, fontWeights, structure, zIndex } from '@/styles'
import { Button } from '@/components/button'
import { toUnit } from '@/utils/styles'
import {
  SearchIcon,
  ListingIcon,
  FavouritesIcon,
  ProfileIcon,
  PricingIcon,
  SettingsIcon,
  SupportIcon,
  TenantsIcon,
  ChatIcon,
  ShieldIcon,
  LogoutIcon,
  TeamIcon,
  Chart,
  BellIcon,
  FileAltIcon,
  IconComponent
} from '@/components/icons'
import { useIncompleteCount, useToaster } from '@/models/toaster/hooks'
import ToggleIcon from './toggle-icon.svg?react'
import LogoIcon from './logo.svg?react'
import PointIcon from './point.svg?react'
import CloseIcon from './close.svg?react'
import { showSecurityModal } from './security-modal/security-modal'
import ChatNotification from '../chat-notification'
import OnboardButton from '../onboarding-toaster/minimized'
import Toaster from '../onboarding-toaster/toaster'

const { WEB_URL } = config

const loadFeatures = () => import('@/components/animations/features').then(res => res.default)

type Expandable = { expanded?: boolean }
type Collapsible = { condensed?: boolean }

const TooltipPoint = styled(PointIcon)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: -6px;
  color: ${colors.blue32};
`

const Wrapper = styled.div<Expandable>`
  --mobile-expanded: ${toUnit(structure.sidebar.widthMobile)};
  --expanded: ${toUnit(structure.sidebar.widthExpanded)};
  --closed: ${toUnit(structure.sidebar.width)};
  --width: ${props => (props.expanded ? 'var(--expanded)' : 'var(--closed)')};

  padding: env(safe-area-inset-top, 0px) 0 env(safe-area-inset-bottom, 0px)
    env(safe-area-inset-left, 0px);
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  background-color: ${colors.secondary};
  z-index: ${zIndex.sidebar};
  height: 100vh;
  display: flex;
  flex-direction: column;
  width: calc(var(--width) + env(safe-area-inset-left, 0px));
  transition: all 0.2s ease;

  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    left: unset;
    right: 0;
    ${props => !props.expanded && `transform: translateX(105%)`};
    --width: 100%;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    height: 100%;
    overflow-y: auto;
  }

  @media print {
    display: none;
  }
`

const NoScroll = createGlobalStyle`
  html {
    overflow: hidden;
    position: fixed;
    max-height: 100%;
  }
`

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  padding: 0 16px 16px;
`

const Toggle = styled.button<Expandable>`
  border: none;
  position: absolute;
  background: ${colors.blue16};
  border-radius: 0 6px 6px 0;
  width: 20px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  top: 50%;
  right: -10px;

  cursor: pointer;

  svg {
    height: 10px;
    width: 10px;
    transition: transform 0.2s ease;
    transform: ${props =>
      props.expanded ? 'rotate(90deg) translateY(1px)' : 'rotate(-90deg) translateY(0px)'};
  }

  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    display: none;
  }
`

const Tooltip = styled.span`
  height: 36px;
  margin: 0;
  padding: 10px 10px;
  background: ${colors.blue32};
  box-shadow:
    0px 10px 15px -3px rgba(0, 0, 0, 0.1),
    0px 4px 6px -2px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  color: ${colors.white};
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  white-space: nowrap;
  position: absolute;
  left: 56px;
  pointer-events: none;
  transform-origin: left;
  transform: scale(0.8) translateY(-50%);
  opacity: 0;
  /* Fixes odd bug with scrollbar showing on hover white keep animation */
  top: -9999px;
  transition:
    transform 100ms ease-in-out,
    opacity 100ms ease-in-out;
  z-index: 10;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: none;
  }
`

const ItemLabel = styled.span`
  color: ${colors.grey64};
  margin: 0 0 0 14px;
  white-space: nowrap;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  transition: color 0.2s ease-in-out;
`

const ItemWrapper = styled(NavLink).withConfig<Collapsible>({
  shouldForwardProp: (prop, defaultValidatorFn) =>
    (defaultValidatorFn(prop) && prop !== 'condensed') || prop === 'isActive'
})`
  display: flex;
  align-items: center;
  background-color: ${colors.blue16};
  padding: 12px 12px;
  cursor: pointer;
  position: relative;
  overflow-x: hidden;
  border-radius: 8px;

  &:hover,
  &:focus {
    overflow-x: visible;
    background-color: ${colors.blue24};

    ${Tooltip} {
      top: 50%;
      opacity: 1;
      transform: scale(1) translateY(-50%);
    }

    > div:last-of-type {
      > p {
        color: ${colors.white};
      }
    }
  }

  &:active {
    background-color: ${colors.blue32};
  }

  &[aria-current='page'] {
    background-color: ${colors.blue32};
    &:hover,
    &:focus {
      background-color: ${colors.blue32};
    }
    ${ItemLabel} {
      color: ${colors.white};
      font-weight: ${fontWeights.medium};
    }
  }

  ${props =>
    props.condensed &&
    `
    padding: 12px 12px;
  `}
  transition: background-color 0.2s ease-in-out;
`

const ClickWrapper = styled.div`
  display: flex;
  align-items: center;
  pointer-events: none;
`

const ChatNotificationWrapper = styled.div`
  position: absolute;
  z-index: 1;
`

const ItemIconWrapper = styled.div`
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  color: ${colors.white};
`

const ToggleWrapper = styled.div<Expandable>`
  margin: 28px 0 60px;
  position: relative;
  display: flex;
  padding: 0 28px;
  align-items: center;
  justify-content: space-between;
  height: 36px;
  left: 0;
  width: 100%;
  display: flex;
  transition: transform 0.2s ease;
`

const Logos = styled.div`
  display: flex;
  align-items: center;
`

const LogoWrapper = styled.a<Expandable>`
  display: flex;
  align-items: center;
  cursor: pointer;
  > svg {
    > path:last-of-type {
      transition: opacity 0.2s ease;
      opacity: ${props => (props.expanded ? 1 : 0)};
    }
  }
`

const ShieldWrapper = styled(Button)<Expandable>`
  margin-left: 12px;
  width: 16px;

  transition:
    visibility 0.15s,
    opacity 0.2s ease;
  ${props =>
    !props.expanded &&
    `
    opacity: 0;
    visibility: hidden;
  `}

  &:hover {
    opacity: 0.8;
  }
`

const LineSpacer = styled.div`
  height: 1px;
  background-color: ${colors.white};
  margin-top: 16px;
  margin-bottom: 16px;
  opacity: 15%;
`

const LogoutIconWhite = styled(LogoutIcon)`
  path {
    fill: ${colors.white};
  }
`

const TeamIconWhite = styled(TeamIcon)`
  path {
    fill: ${colors.white};
  }
`

const StyledButton = styled.div`
  background: rgba(245, 245, 245, 0.1);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease-in-out;

  svg {
    color: ${colors.white};
  }

  &:hover {
    opacity: 0.6;
  }
`

const Buttons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

type MenuOptionsProps = Expandable &
  Collapsible & {
    id: string
    to?: string
    href?: string
    activePath?: string[]
    icon?: IconComponent
    isChat?: boolean
    title?: string
    target?: '_self' | '_blank'
    role?: string[]
  }

const MenuOption: React.FC<MenuOptionsProps> = ({
  id,
  to,
  href,
  activePath = [],
  icon: Icon,
  title,
  isChat,
  expanded,
  condensed,
  target = '_self'
}) => {
  const isActive = (_: match, location: Location) =>
    activePath.some(path => new RegExp('^' + path).test(location.pathname))
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.desktopSmall})`)
  const hidden = !expanded && isMobile
  const extra = to ? { to, isActive } : {}
  return (
    <>
      <ItemWrapper
        id={id}
        as={to ? NavLink : 'a'}
        {...extra}
        href={href}
        aria-label={title}
        condensed={condensed}
        onClick={(e: React.MouseEvent<HTMLElement>) => (isMobile ? undefined : e.stopPropagation())}
        aria-hidden={hidden}
        tabIndex={hidden ? -1 : 0}
        target={target}
      >
        <ClickWrapper>
          {isChat && (
            <ChatNotificationWrapper>
              <ChatNotification top="-18px" right="-29px" load />
            </ChatNotificationWrapper>
          )}
          {Icon && (
            <ItemIconWrapper>
              <Icon width={20} height={20} aria-hidden />
            </ItemIconWrapper>
          )}
          {expanded && (
            <m.span
              initial={{ opacity: 0, lineHeight: '20px' }}
              animate={{ opacity: 1 }}
              transition={{ type: 'spring', duration: 0.8 }}
            >
              <ItemLabel>{title}</ItemLabel>
            </m.span>
          )}
          {!expanded && (
            <Tooltip>
              <TooltipPoint aria-hidden />
              {title}
            </Tooltip>
          )}
        </ClickWrapper>
      </ItemWrapper>
    </>
  )
}

const ButtonWrapper = styled.div`
  display: none;
  opacity: 1;
  transition: opacity 0.2s ease-in-out;
  > div > button {
    border: 2px solid ${colors.grey88} !important;
    padding: 0;
  }
  &[aria-hidden='true'] {
    opacity: 0;
    pointer-events: none;
  }
  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    display: block;
  }
`

const Overlay = styled.div<Expandable>`
  -webkit-tap-highlight-color: transparent;
  display: none;
  width: 100vw;
  height: 100%;
  top: 0;
  left: 0;
  cursor: pointer;
  background: ${props => (props.expanded ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0)')};
  transition: background 0.2s ease;
  position: fixed;
  z-index: ${zIndex.sidebar - 1};
  transform: translateX(${props => (props.expanded ? 0 : '-100%')});

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: block;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    min-height: 100%;
  }

  @media print {
    display: none;
  }
`

type SidebarProps = Expandable & {
  setSidebar: React.Dispatch<React.SetStateAction<boolean>>
}

const Sidebar: React.FC<SidebarProps> = ({ expanded, setSidebar }) => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const role = useRole()
  const isTenant = role === 'tenant'
  const userRole = isTenant ? 'renter' : 'landlord'
  const { open, enabled, hide_completed, setOpen, setEnabled, setHideCompleted } = useToaster()
  const { data, count } = useIncompleteCount()
  const currentLang = i18n.languages[0]
  const localizedWebUrl = currentLang === 'en' ? WEB_URL : `${WEB_URL}/${currentLang}`
  const isFrench = currentLang === 'fr'
  const condensed = expanded && isFrench
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.desktopSmall})`)

  const TENANT_MENU_OPTIONS: MenuOptionsProps[] = [
    {
      title: t('b.sidebar.view.browse_listings.link', 'Browse Listings'),
      id: 'menu-manage-logged-browse',
      to: undefined,
      href: `${localizedWebUrl}/rental-listings`,
      target: '_self',
      activePath: [],
      icon: SearchIcon
    },
    {
      title: t('common.chat', 'Chat'),
      id: 'menu-manage-logged-chat',
      to: '/chat',
      href: undefined,
      target: '_self',
      activePath: ['/chat'],
      icon: ChatIcon
    },
    {
      title: t('b.sidebar.view.rentals.link', 'Your Rentals'),
      id: 'menu-manage-logged-rentals',
      to: '/rentals',
      href: undefined,
      target: '_self',
      activePath: ['/rental'],
      icon: ListingIcon
    },
    {
      title: t('b.sidebar.view.favourites.link', 'Favourites'),
      id: 'menu-manage-logged-favourites',
      to: undefined,
      href: `${localizedWebUrl}/favourites`,
      target: '_self',
      activePath: [],
      icon: FavouritesIcon
    },
    {
      title: t('b.sidebar.view.saved_alerts.link', 'Saved Alerts'),
      id: 'lakshimi-saved-alerts-click',
      to: undefined,
      href: `${localizedWebUrl}/saved-alerts`,
      target: '_blank',
      activePath: [],
      icon: BellIcon
    },
    {
      title: t('b.sidebar.view.settings.link', 'Settings'),
      id: 'menu-manage-logged-settings',
      to: isMobile ? '/settings' : '/settings/payment-settings',
      href: undefined,
      target: '_self',
      activePath: ['/settings', '/manage'],
      icon: SettingsIcon
    }
  ]

  const LANDLORD_MENU_OPTIONS: MenuOptionsProps[] = [
    {
      title: t('b.sidebar.view.listings.link', 'Listings'),
      id: 'menu-manage-logged-listings',
      to: '/listings',
      activePath: ['/listing'],
      icon: ListingIcon
    },
    {
      title: t('l.tenants.list.rented.title', 'Rented'),
      id: 'menu-manage-logged-tenants',
      to: '/tenants',
      activePath: ['/tenant'],
      icon: TenantsIcon
    },
    {
      title: t('common.chat', 'Chat'),
      id: 'menu-manage-logged-chat',
      to: '/chat',
      activePath: ['/chat'],
      icon: ChatIcon
    },
    {
      title: t('l.standalone_trustscore.sidebar.text', 'Trust Score'),
      id: 'menu-manage-standalone-trustscore',
      to: '/trust-score',
      activePath: ['/trust-score'],
      icon: FileAltIcon
    },
    {
      title: t('b.sidebar.view.credit_usage.link', 'Credit Usage'),
      id: 'menu-manage-logged-usage-credits',
      to: '/usage-credits/products/my-products',
      activePath: ['/usage'],
      icon: Chart
    },
    {
      title: t('b.sidebar.view.settings.link', 'Settings'),
      id: 'menu-manage-logged-settings',
      to: isMobile ? '/settings' : '/settings/plan-settings',
      activePath: ['/settings', '/manage'],
      icon: SettingsIcon
    }
  ]

  const MENU_OPTIONS_MOBILE: MenuOptionsProps[] = [
    {
      title: t('b.sidebar.view.profile.link', 'Profile'),
      id: 'menu-manage-logged-profile',
      to: '/profile',
      href: undefined,
      target: '_self',
      activePath: ['/profile'],
      icon: ProfileIcon,
      role: ['renter', 'landlord']
    },
    {
      title: t('l.menu.view.team.link', 'Team'),
      id: 'menu-manage-logged-team',
      to: '/manage/users',
      href: undefined,
      target: '_self',
      activePath: ['/manage/users'],
      icon: TeamIconWhite,
      role: ['landlord']
    },
    {
      title: t('b.sidebar.view.saved_alerts.link', 'Saved Alerts'),
      id: 'lakshimi-saved-alerts-click',
      to: undefined,
      href: `${localizedWebUrl}/saved-alerts`,
      target: '_blank',
      activePath: [],
      icon: BellIcon,
      role: ['landlord']
    },
    {
      title: t('b.sidebar.view.pricing.link', 'Pricing'),
      id: 'menu-manage-logged-pricing',
      to: undefined,
      href: `${localizedWebUrl}/pricing`,
      target: '_self',
      activePath: [],
      icon: PricingIcon,
      role: ['landlord']
    },
    {
      title: t('b.sidebar.view.support.link', 'Support'),
      id: 'menu-manage-logged-support',
      to: '/support',
      href: undefined,
      target: '_self',
      activePath: ['/support'],
      icon: SupportIcon,
      role: ['renter', 'landlord']
    }
  ]

  const logoutAndRedirect = async () => {
    await dispatch.auth.logout()
    history.replace('/')
  }

  const toggleSidebar = () => setSidebar(current => !current)
  return (
    <LazyMotion features={loadFeatures}>
      <Overlay expanded={expanded} onClick={toggleSidebar} />
      <Wrapper expanded={expanded} onClick={toggleSidebar}>
        {isMobile && expanded && <NoScroll />}
        <ToggleWrapper expanded={expanded}>
          <Logos>
            {isTenant ? (
              <LogoWrapper
                expanded={expanded}
                href={localizedWebUrl}
                target="_blank"
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  if (!isMobile) e.stopPropagation()
                }}
              >
                <LogoIcon />
              </LogoWrapper>
            ) : (
              <LogoWrapper
                expanded={expanded}
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  history.push('/listings')
                  if (!isMobile) e.stopPropagation()
                }}
              >
                <LogoIcon />
              </LogoWrapper>
            )}
            <ShieldWrapper
              expanded={expanded}
              disabled={!expanded}
              theme="text"
              space="none"
              id={`application-shield-click-logged-${userRole}`}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                if (!isMobile) e.stopPropagation()
                showSecurityModal({ userRole })
              }}
            >
              <ShieldIcon />
            </ShieldWrapper>
          </Logos>
          <Buttons>
            <ButtonWrapper aria-hidden={!expanded}>
              <Box style={{ width: '40px', height: '40px' }}>
                <StyledButton
                  aria-label="Close sidebar"
                  aria-hidden={!expanded}
                  tabIndex={!expanded ? -1 : 0}
                >
                  <CloseIcon aria-hidden />
                </StyledButton>
              </Box>
            </ButtonWrapper>
          </Buttons>
        </ToggleWrapper>
        <InnerWrapper>
          <Box gap="xxxsmall" display="flex" flexDirection="column">
            {isTenant
              ? TENANT_MENU_OPTIONS.map((item, index) => {
                  return (
                    <MenuOption
                      expanded={expanded}
                      title={item.title}
                      id={item.id}
                      to={item.to}
                      href={item.href}
                      target={item.target}
                      activePath={item.activePath}
                      icon={item.icon}
                      key={`${index}-${item.id}`}
                      isChat={item.id === 'menu-manage-logged-chat'}
                    />
                  )
                })
              : LANDLORD_MENU_OPTIONS.map((item, index) => {
                  return (
                    <MenuOption
                      expanded={expanded}
                      title={item.title}
                      id={item.id}
                      to={item.to}
                      activePath={item.activePath}
                      icon={item.icon}
                      key={`${index}-${item.id}`}
                      isChat={item.id === 'menu-manage-logged-chat'}
                    />
                  )
                })}
            {isMobile && (
              <>
                <LineSpacer />
                {MENU_OPTIONS_MOBILE.filter(item => item.role?.includes(userRole)).map(
                  (item, index) => (
                    <MenuOption
                      expanded={expanded}
                      title={item.title}
                      id={item.id}
                      to={item.to}
                      href={item.href}
                      target={item.target}
                      activePath={item.activePath}
                      icon={item.icon}
                      key={`${index}-${item.id}`}
                      isChat={item.id === 'menu-manage-logged-chat'}
                    />
                  )
                )}
                <Box onClick={logoutAndRedirect}>
                  <MenuOption
                    expanded={expanded}
                    id="menu-manage-logged-logout"
                    icon={LogoutIconWhite}
                    title={t('l.menu.view.logout.link', 'Logout')}
                    condensed={condensed}
                  />
                </Box>
              </>
            )}
          </Box>
          {!isMobile && !isTenant && enabled && (
            <>
              {open && (
                <Toaster
                  data={data}
                  setOpen={setOpen}
                  setEnabled={setEnabled}
                  hideCompleted={hide_completed}
                  setHideCompleted={setHideCompleted}
                  count={count}
                />
              )}
              <OnboardButton setOpen={setOpen} count={count} />
            </>
          )}
        </InnerWrapper>
        <Toggle
          aria-label="Toggle sidebar"
          aria-pressed={expanded}
          expanded={expanded}
          type="button"
        >
          <ToggleIcon aria-hidden />
        </Toggle>
      </Wrapper>
    </LazyMotion>
  )
}

export default Sidebar
